import React, { useEffect, useMemo, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  InputAdornment,
  IconButton,
  MenuItem,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  Typography
} from "@mui/material";
import AddRoundedIcon from "@mui/icons-material/AddRounded";
import DeleteOutlineRoundedIcon from "@mui/icons-material/DeleteOutlineRounded";
import EditRoundedIcon from "@mui/icons-material/EditRounded";
import SearchRoundedIcon from "@mui/icons-material/SearchRounded";
import AutoFixHighRoundedIcon from "@mui/icons-material/AutoFixHighRounded";
import DescriptionRoundedIcon from "@mui/icons-material/DescriptionRounded";
import EventRoundedIcon from "@mui/icons-material/EventRounded";
import PrintRoundedIcon from "@mui/icons-material/PrintRounded";
import { useNavigate, useSearchParams } from "react-router-dom";
import { adminApi } from "../api/client";
import { eventMasterSeed } from "../data/eventMasterSeed";
import { OptionItem, ResourceConfig, ResourceField, ResourceListItem } from "../types";
import { getEventParticipantLabels } from "../utils/eventParticipantLabels";

type FormValue = string | number | boolean | null;

function normalizeJsonValue(rawValue: unknown) {
  if (!rawValue) {
    return "";
  }

  if (typeof rawValue === "string") {
    try {
      return JSON.stringify(JSON.parse(rawValue), null, 2);
    } catch (error) {
      return rawValue;
    }
  }

  return JSON.stringify(rawValue, null, 2);
}

function getInitialFormState(config: ResourceConfig) {
  return config.formFields.reduce<Record<string, FormValue>>((acc, field) => {
    if (field.type === "checkbox") {
      acc[field.name] = false;
    } else {
      acc[field.name] = "";
    }
    return acc;
  }, {});
}

function coerceFieldValue(field: ResourceField, value: FormValue) {
  if (field.type === "number") {
    return value === "" ? null : Number(value);
  }

  if (field.type === "checkbox") {
    return Boolean(value);
  }

  if (field.type === "json" && typeof value === "string" && value.trim()) {
    try {
      return JSON.parse(value);
    } catch (error) {
      return value;
    }
  }

  return value === "" ? null : value;
}

function formatInputValue(field: ResourceField, value: string) {
  if (field.pattern === "^\\d{10}$") {
    return value.replace(/\D/g, "").slice(0, field.maxLength || 10);
  }

  return field.maxLength ? value.slice(0, field.maxLength) : value;
}

function formatDateTimeLocalValue(value: unknown) {
  if (!value) {
    return "";
  }

  const raw = String(value).trim();

  if (!raw) {
    return "";
  }

  const normalized = raw.replace(" ", "T");
  const dateTimeMatch = normalized.match(/^(\d{4}-\d{2}-\d{2}T\d{2}:\d{2})/);

  if (dateTimeMatch) {
    return dateTimeMatch[1];
  }

  const parsed = new Date(raw);

  if (Number.isNaN(parsed.getTime())) {
    return "";
  }

  const local = new Date(parsed.getTime() - parsed.getTimezoneOffset() * 60000);
  return local.toISOString().slice(0, 16);
}

function normalizeFieldValueForForm(field: ResourceField, rawValue: unknown): FormValue {
  if (field.type === "checkbox") {
    return Boolean(rawValue);
  }

  if (field.type === "json") {
    return normalizeJsonValue(rawValue);
  }

  if (field.type === "datetime-local") {
    return formatDateTimeLocalValue(rawValue);
  }

  if (field.type === "date" && rawValue) {
    return String(rawValue).slice(0, 10);
  }

  if (field.type === "select" && field.optionsResource && rawValue !== null && rawValue !== undefined && rawValue !== "") {
    return Number(rawValue);
  }

  return (rawValue as FormValue) ?? "";
}

function normalizeFieldValueForSubmit(field: ResourceField, value: FormValue) {
  if (field.type === "datetime-local" && typeof value === "string" && value) {
    return value.length === 16 ? `${value.replace("T", " ")}:00` : value.replace("T", " ");
  }

  return coerceFieldValue(field, value);
}

function normalizeOptionLabel(resource: string | undefined, label: string) {
  if (resource === "organization-profiles" && label.trim().toLowerCase() === ["ash", "vish events"].join("-")) {
    return "Eventena";
  }

  return label;
}

function getEventTypeLabel(formState: Record<string, FormValue>, optionCache: Record<string, OptionItem[]>) {
  const eventTypeId = formState.event_type_id;
  const selectedEventType = optionCache.event_type_id?.find((option) => String(option.value) === String(eventTypeId));
  const raw = selectedEventType?.raw || {};

  return raw.type_code || raw.type_name || selectedEventType?.label || "";
}

function firstFilled(...values: unknown[]) {
  const value = values.find((item) => String(item || "").trim());
  return value === undefined ? "" : String(value).trim();
}

function fillFromSelection(nextValue: unknown) {
  return firstFilled(nextValue);
}

const autoCodeConfig: Record<string, { field: string; prefix: string }> = {
  "quotation-templates": { field: "template_code", prefix: "TPL" },
  "service-categories": { field: "category_code", prefix: "CAT" }
};

function createNextCode(resource: string, records: ResourceListItem[]) {
  const config = autoCodeConfig[resource];

  if (!config) {
    return "";
  }

  const maxNumber = records.reduce((max, record) => {
    const rawCode = String(record[config.field] || "");
    const match = rawCode.match(/(\d+)$/);
    return match ? Math.max(max, Number(match[1])) : max;
  }, 0);

  return `${config.prefix}-${String(maxNumber + 1).padStart(3, "0")}`;
}

interface ResourceManagerProps {
  config: ResourceConfig;
}

export default function ResourceManager({ config }: ResourceManagerProps) {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [items, setItems] = useState<ResourceListItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [query, setQuery] = useState("");
  const [appliedQuery, setAppliedQuery] = useState("");
  const [filterValues, setFilterValues] = useState<Record<string, FormValue>>({});
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [totalItems, setTotalItems] = useState(0);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingItem, setEditingItem] = useState<ResourceListItem | null>(null);
  const [formState, setFormState] = useState<Record<string, FormValue>>(getInitialFormState(config));
  const [formErrors, setFormErrors] = useState<Record<string, string>>({});
  const [optionCache, setOptionCache] = useState<Record<string, OptionItem[]>>({});
  const [deleteTarget, setDeleteTarget] = useState<ResourceListItem | null>(null);
  const [seedingMasters, setSeedingMasters] = useState(false);
  const [eventQuotationIds, setEventQuotationIds] = useState<Record<string, number>>({});
  const [eventCreatePrefillApplied, setEventCreatePrefillApplied] = useState(false);
  const eventPrefillCustomerId = searchParams.get("customerId");
  const canSeedEventMasters = config.resource === "service-categories" || config.resource === "service-catalog-items";
  const eventParticipantLabels = getEventParticipantLabels(config.resource === "events" ? getEventTypeLabel(formState, optionCache) : "");
  const isEventLockedByQuotation = Boolean(
    config.resource === "events" &&
    editingItem?.id &&
    eventQuotationIds[String(editingItem.id)]
  );
  const isLockedEventRelationField = (fieldName: string) => (
    isEventLockedByQuotation && (fieldName === "customer_id" || fieldName === "event_type_id")
  );
  const getFieldLabel = (field: ResourceField) => {
    if (config.resource !== "events") {
      return field.label;
    }

    if (field.name === "groom_name") {
      return eventParticipantLabels.primary;
    }

    if (field.name === "bride_name") {
      return eventParticipantLabels.secondary;
    }

    return field.label;
  };

  const loadItems = async (
    nextPage = page,
    nextRowsPerPage = rowsPerPage,
    nextQuery = appliedQuery,
    nextFilters = filterValues
  ) => {
    try {
      setLoading(true);
      setError(null);
      const params = new URLSearchParams({
        page: String(nextPage + 1),
        limit: String(nextRowsPerPage)
      });

      if (nextQuery) {
        params.set("search", nextQuery);
      }

      Object.entries(nextFilters).forEach(([key, value]) => {
        if (value !== "" && value !== null && value !== undefined) {
          params.set(key, String(value));
        }
      });

      const suffix = `?${params.toString()}`;
      const response = await adminApi.listResource<ResourceListItem>(config.resource, suffix);
      setItems(response.items);
      setTotalItems(response.pagination.total);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to load records");
    } finally {
      setLoading(false);
    }
  };

  const loadOptions = async () => {
    const fieldMap = new Map<string, ResourceField>();
    [...(config.listFilters || []), ...config.formFields].forEach((field) => {
      if (field.optionsResource && !fieldMap.has(field.name)) {
        fieldMap.set(field.name, field);
      }
    });
    const fieldsWithOptions = Array.from(fieldMap.values());

    const entries = await Promise.all(
      fieldsWithOptions.map(async (field) => {
        try {
          const response = await adminApi.listResource<ResourceListItem>(field.optionsResource!, "?limit=200");
          const options = response.items.map((item) => ({
            label: normalizeOptionLabel(field.optionsResource, String(item[field.optionLabelKey || "name"] ?? item.id)),
            value: Number(item[field.optionValueKey || "id"] ?? item.id),
            raw: item
          }));
          return [field.name, options] as const;
        } catch (error) {
          return [field.name, []] as const;
        }
      })
    );

    setOptionCache(Object.fromEntries(entries));
  };

  useEffect(() => {
    void loadOptions();
    setFilterValues({});
    setPage(0);
  }, [config.resource]);

  useEffect(() => {
    if (query === "") {
      setPage(0);
      setAppliedQuery("");
    }
  }, [query]);

  useEffect(() => {
    void loadItems();
  }, [config.resource, page, rowsPerPage, appliedQuery, filterValues]);

  useEffect(() => {
    setEventCreatePrefillApplied(false);
  }, [config.resource, eventPrefillCustomerId]);

  useEffect(() => {
    if (config.resource !== "events" || !items.length) {
      setEventQuotationIds({});
      return;
    }

    let cancelled = false;

    const loadEventQuotations = async () => {
      const entries = await Promise.all(
        items.map(async (item) => {
          try {
            const quotations = await adminApi.get<Array<{ id?: number }>>(`/quotations?eventId=${item.id}`);
            const quotationId = quotations.find((quotation) => quotation.id)?.id;
            return quotationId ? [String(item.id), quotationId] as const : null;
          } catch (error) {
            return null;
          }
        })
      );

      if (!cancelled) {
        setEventQuotationIds(Object.fromEntries(entries.filter((entry): entry is readonly [string, number] => Boolean(entry))));
      }
    };

    void loadEventQuotations();

    return () => {
      cancelled = true;
    };
  }, [config.resource, items]);

  const columns = useMemo(() => config.columns, [config.columns]);

  const renderColumnValue = (columnKey: string, value: unknown) => {
    const field = config.formFields.find((item) => item.name === columnKey && item.type === "select");
    const options = field ? optionCache[field.name] || [] : [];
    const option = options.find((item) => String(item.value) === String(value));

    if (option) {
      return option.label;
    }

    if (typeof value === "boolean") {
      return (
        <Chip
          size="small"
          color={value ? "success" : "default"}
          label={value ? "Yes" : "No"}
        />
      );
    }

    return String(value ?? "-");
  };

  const handleSearch = () => {
    const nextQuery = query.trim();
    setPage(0);
    setAppliedQuery(nextQuery);

    if (page === 0 && nextQuery === appliedQuery) {
      void loadItems(0, rowsPerPage, nextQuery, filterValues);
    }
  };

  const handleFilterChange = (name: string, value: FormValue) => {
    setPage(0);
    setFilterValues((current) => ({
      ...current,
      [name]: value
    }));
  };

  const handleChangePage = (_event: unknown, nextPage: number) => {
    setPage(nextPage);
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(Number(event.target.value));
    setPage(0);
  };

  const openCreateDialog = async (initialValues: Record<string, FormValue> = {}) => {
    setEditingItem(null);
    const nextState = getInitialFormState(config);
    const codeConfig = autoCodeConfig[config.resource];

    if (codeConfig) {
      try {
        const response = await adminApi.listResource<ResourceListItem>(config.resource, "?limit=1000");
        nextState[codeConfig.field] = createNextCode(config.resource, response.items);
      } catch (error) {
        nextState[codeConfig.field] = createNextCode(config.resource, items);
      }
    }

    if (config.resource === "customers") {
      nextState.city = "Kolhapur";
      nextState.state = "Maharashtra";
      nextState.country = "India";
    }

    if (config.resource === "custom-questions") {
      nextState.answer_type = "textarea";
      nextState.is_active = true;
      nextState.display_order = items.length + 1;
    }

    Object.entries(initialValues).forEach(([key, value]) => {
      nextState[key] = value;
    });

    if (config.resource === "events" && initialValues.customer_id) {
      Object.assign(nextState, buildEventCustomerDefaults(nextState, initialValues.customer_id));
    }

    setFormState(nextState);
    setFormErrors({});
    setDialogOpen(true);
  };

  const openEditDialog = (item: ResourceListItem) => {
    const nextState = getInitialFormState(config);

    config.formFields.forEach((field) => {
      nextState[field.name] = normalizeFieldValueForForm(field, item[field.name]);
    });

    setEditingItem(item);
    setFormState(nextState);
    setFormErrors({});
    setDialogOpen(true);
  };

  const closeDialog = (_event?: object, reason?: "backdropClick" | "escapeKeyDown") => {
    if (reason === "backdropClick") {
      return;
    }

    setDialogOpen(false);
    setEditingItem(null);
    setFormErrors({});
  };

  const buildEventCustomerDefaults = (current: Record<string, FormValue>, customerId: FormValue): Record<string, FormValue> => {
    if (config.resource !== "events" || customerId === "" || customerId === null) {
      return {};
    }

    const selectedCustomer = optionCache.customer_id?.find((option) => String(option.value) === String(customerId));
    const customer = selectedCustomer?.raw || {};
    const customerName = firstFilled(customer.display_name, selectedCustomer?.label);
    const familyName = firstFilled(customer.family_name, customerName);
    const addressLine = [customer.address_line1, customer.address_line2].map((item) => String(item || "").trim()).filter(Boolean).join(", ");

    return {
      host_family_name: fillFromSelection(familyName),
      groom_name: fillFromSelection(customerName),
      venue_address_line1: fillFromSelection(addressLine),
      venue_city: fillFromSelection(customer.city),
      venue_state: fillFromSelection(customer.state),
      venue_postal_code: fillFromSelection(customer.postal_code),
      venue_country: fillFromSelection(customer.country),
      customer_notes: fillFromSelection(customer.notes)
    };
  };

  const buildEventTypeDefaults = (eventTypeId: FormValue): Record<string, FormValue> => {
    if (config.resource !== "events" || eventTypeId === "" || eventTypeId === null) {
      return {};
    }

    const selectedEventType = optionCache.event_type_id?.find((option) => String(option.value) === String(eventTypeId));
    const eventTypeName = firstFilled(selectedEventType?.raw?.type_name, selectedEventType?.label);

    return {
      occasion_label: fillFromSelection(eventTypeName),
      title: fillFromSelection(eventTypeName ? `${eventTypeName} Event` : "")
    };
  };

  useEffect(() => {
    const customerOptionsLoaded = Object.prototype.hasOwnProperty.call(optionCache, "customer_id");

    if (config.resource !== "events" || eventCreatePrefillApplied || !eventPrefillCustomerId || !customerOptionsLoaded) {
      return;
    }

    setEventCreatePrefillApplied(true);
    void openCreateDialog({ customer_id: Number(eventPrefillCustomerId) });

    const nextParams = new URLSearchParams(searchParams);
    nextParams.delete("customerId");
    setSearchParams(nextParams, { replace: true });
  }, [config.resource, eventCreatePrefillApplied, eventPrefillCustomerId, optionCache, searchParams, setSearchParams]);

  const handleFieldChange = (name: string, value: FormValue) => {
    setFormState((current) => ({
      ...current,
      [name]: value,
      ...(name === "customer_id" ? buildEventCustomerDefaults(current, value) : {}),
      ...(name === "event_type_id" ? buildEventTypeDefaults(value) : {})
    }));
    setFormErrors((current) => {
      if (!current[name]) {
        return current;
      }

      const next = { ...current };
      delete next[name];
      return next;
    });
  };

  const validateForm = () => {
    const errors: Record<string, string> = {};
    config.formFields.forEach((field) => {
      if (field.generatedOnCreate && !editingItem) {
        return;
      }

      const value = formState[field.name];
      const stringValue = value === null || value === undefined ? "" : String(value).trim();

      if ((field.required || (field.requiredOnCreate && !editingItem)) && !stringValue) {
        errors[field.name] = `${getFieldLabel(field)} is required`;
        return;
      }

      if (!stringValue) {
        return;
      }

      if (field.type === "email" && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(stringValue)) {
        errors[field.name] = "Enter a valid email address";
      } else if (field.pattern && !new RegExp(field.pattern).test(stringValue)) {
        errors[field.name] = field.patternMessage || `${getFieldLabel(field)} is invalid`;
      } else if (field.minLength && stringValue.length < field.minLength) {
        errors[field.name] = `${getFieldLabel(field)} must be at least ${field.minLength} characters`;
      } else if (field.maxLength && stringValue.length > field.maxLength) {
        errors[field.name] = `${getFieldLabel(field)} must be ${field.maxLength} characters or less`;
      } else if (field.type === "number") {
        const numericValue = Number(value);
        if (!Number.isFinite(numericValue)) {
          errors[field.name] = `${getFieldLabel(field)} must be a number`;
        } else if (field.min !== undefined && numericValue < field.min) {
          errors[field.name] = `${getFieldLabel(field)} must be at least ${field.min}`;
        } else if (field.max !== undefined && numericValue > field.max) {
          errors[field.name] = `${getFieldLabel(field)} must be ${field.max} or less`;
        }
      }
    });

    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async () => {
    if (!validateForm()) {
      return;
    }

    const payload = config.formFields.reduce<Record<string, unknown>>((acc, field) => {
      if (field.generatedOnCreate && !editingItem) {
        return acc;
      }

      if (isLockedEventRelationField(field.name)) {
        return acc;
      }

      const value = normalizeFieldValueForSubmit(field, formState[field.name]);
      if (value !== null && value !== "") {
        acc[field.name] = value;
      } else if (field.type === "checkbox") {
        acc[field.name] = false;
      }
      return acc;
    }, {});

    try {
      setLoading(true);
      setError(null);

      if (editingItem?.id) {
        await adminApi.updateResource(config.resource, String(editingItem.id), payload);
      } else {
        await adminApi.createResource(config.resource, payload);
      }

      closeDialog();
      await loadItems();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to save record");
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id: unknown) => {
    try {
      setLoading(true);
      await adminApi.deleteResource(config.resource, String(id));
      setDeleteTarget(null);
      await loadItems();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to delete record");
    } finally {
      setLoading(false);
    }
  };

  const openQuotationForEvent = (item: ResourceListItem) => {
    const params = new URLSearchParams();
    params.set("eventId", String(item.id || ""));

    if (item.customer_id) {
      params.set("customerId", String(item.customer_id));
    }

    navigate(`/admin/quotations/new?${params.toString()}`);
  };

  const openEventQuotationPrint = (quotationId: number) => {
    navigate(`/admin/quotations/${quotationId}/print`);
  };

  const openEventForCustomer = (item: ResourceListItem) => {
    navigate(`/admin/events?customerId=${item.id}`);
  };

  const seedEventMasters = async () => {
    try {
      setSeedingMasters(true);
      setError(null);

      const eventTypesResponse = await adminApi.listResource<ResourceListItem>("event-types", "?limit=500");
      const eventTypeByCode = new Map(
        eventTypesResponse.items.map((eventType) => [String(eventType.type_code || eventType.type_name || "").trim().toLowerCase(), eventType])
      );
      const categoriesResponse = await adminApi.listResource<ResourceListItem>("service-categories", "?limit=1000");
      const existingCategories = categoriesResponse.items;
      const categoryByCode = new Map(existingCategories.map((category) => [String(category.category_code || "").toLowerCase(), category]));

      for (const categorySeed of eventMasterSeed) {
        const eventType = eventTypeByCode.get(categorySeed.event_type_code.toLowerCase());
        const eventTypeId = eventType?.id ? Number(eventType.id) : null;
        const existingCategory = categoryByCode.get(categorySeed.category_code.toLowerCase());

        if (!categoryByCode.has(categorySeed.category_code.toLowerCase())) {
          const createdCategory = await adminApi.createResource<ResourceListItem>("service-categories", {
            event_type_id: eventTypeId,
            category_code: categorySeed.category_code,
            category_name: categorySeed.category_name,
            description: categorySeed.description,
            default_display_order: categorySeed.default_display_order,
            is_active: categorySeed.is_active
          });

          categoryByCode.set(categorySeed.category_code.toLowerCase(), createdCategory);
        } else if (existingCategory?.id && eventTypeId && !existingCategory.event_type_id) {
          const updatedCategory = await adminApi.updateResource<ResourceListItem>("service-categories", String(existingCategory.id), {
            event_type_id: eventTypeId,
            category_name: existingCategory.category_name || categorySeed.category_name,
            description: existingCategory.description || categorySeed.description,
            default_display_order: existingCategory.default_display_order ?? categorySeed.default_display_order,
            is_active: existingCategory.is_active ?? categorySeed.is_active
          });

          categoryByCode.set(categorySeed.category_code.toLowerCase(), updatedCategory);
        }
      }

      const catalogResponse = await adminApi.listResource<ResourceListItem>("service-catalog-items", "?limit=2000");
      const existingItemCodes = new Set(catalogResponse.items.map((item) => String(item.item_code || "").toLowerCase()));

      for (const categorySeed of eventMasterSeed) {
        const category = categoryByCode.get(categorySeed.category_code.toLowerCase());
        const categoryId = category?.id;

        if (!categoryId) {
          continue;
        }

        for (const itemSeed of categorySeed.items) {
          if (existingItemCodes.has(itemSeed.item_code.toLowerCase())) {
            continue;
          }

          await adminApi.createResource<ResourceListItem>("service-catalog-items", {
            category_id: categoryId,
            item_code: itemSeed.item_code,
            item_name: itemSeed.item_name,
            description: itemSeed.description,
            default_unit: itemSeed.default_unit,
            default_dimension_text: itemSeed.default_dimension_text,
            default_unit_rate: itemSeed.default_unit_rate,
            pricing_mode: itemSeed.pricing_mode,
            is_active: itemSeed.is_active
          });
          existingItemCodes.add(itemSeed.item_code.toLowerCase());
        }
      }

      await loadItems();
      await loadOptions();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to add event master data");
    } finally {
      setSeedingMasters(false);
    }
  };

  return (
    <Stack spacing={3}>
      <Paper
        elevation={0}
        sx={{
          p: { xs: 2.5, md: 3 },
          borderRadius: 4,
          border: "1px solid rgba(118, 93, 68, 0.12)",
          background: "rgba(255, 250, 244, 0.94)"
        }}
      >
        <Stack
          direction={{ xs: "column", lg: "row" }}
          spacing={2}
          justifyContent="space-between"
          alignItems={{ xs: "stretch", lg: "center" }}
        >
          <Box sx={{ minWidth: 0 }}>
            <Typography variant="h4" sx={{ fontWeight: 800, mb: 1 }}>
              {config.title}
            </Typography>
            <Typography variant="body1" sx={{ color: "text.secondary", maxWidth: 720 }}>
              {config.description}
            </Typography>
          </Box>

          <Stack
            direction={{ xs: "column", sm: "row" }}
            spacing={1.5}
            useFlexGap
            flexWrap="wrap"
            justifyContent={{ xs: "stretch", lg: "flex-end" }}
            sx={{ width: { xs: "100%", lg: "auto" }, maxWidth: { lg: 720 } }}
          >
            <TextField
              size="small"
              placeholder={`Search ${config.title.toLowerCase()}`}
              value={query}
              onChange={(event) => setQuery(event.target.value)}
              sx={{ minWidth: { xs: "100%", sm: 220 }, flex: { xs: "1 1 100%", sm: "1 1 220px" } }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchRoundedIcon sx={{ color: "text.secondary", fontSize: 18 }} />
                  </InputAdornment>
                )
              }}
            />
            {(config.listFilters || []).map((filter) => {
              const selectOptions = filter.options || optionCache[filter.name] || [];

              return (
                <TextField
                  key={filter.name}
                  size="small"
                  label={filter.label}
                  select
                  value={filterValues[filter.name] ?? ""}
                  onChange={(event) => handleFilterChange(filter.name, event.target.value)}
                  sx={{ minWidth: { xs: "100%", sm: 220 }, flex: { xs: "1 1 100%", sm: "1 1 220px" } }}
                >
                  <MenuItem value="">All {filter.label}</MenuItem>
                  {selectOptions.map((option) => (
                    <MenuItem key={String(option.value)} value={option.value}>
                      {option.label}
                    </MenuItem>
                  ))}
                </TextField>
              );
            })}
            <Button variant="outlined" onClick={handleSearch} sx={{ minHeight: 40, px: 2.25, whiteSpace: "nowrap", flex: { xs: "1 1 100%", sm: "0 0 auto" } }}>
              Search
            </Button>
            {canSeedEventMasters ? (
              <Button
                variant="outlined"
                startIcon={<AutoFixHighRoundedIcon />}
                onClick={() => void seedEventMasters()}
                disabled={seedingMasters}
                sx={{ minHeight: 40, px: 2.25, whiteSpace: "nowrap", flex: { xs: "1 1 100%", sm: "0 0 auto" } }}
              >
                {seedingMasters ? "Adding..." : "Add Event Masters"}
              </Button>
            ) : null}
            {config.resource === "custom-questions" ? (
              <Button
                variant="outlined"
                startIcon={<PrintRoundedIcon />}
                onClick={() => navigate("/admin/custom-questions/print")}
                sx={{ minHeight: 40, px: 2.25, whiteSpace: "nowrap", flex: { xs: "1 1 100%", sm: "0 0 auto" } }}
              >
                Print View
              </Button>
            ) : null}
            <Button variant="contained" startIcon={<AddRoundedIcon />} onClick={() => void openCreateDialog()} sx={{ minHeight: 40, px: 2.25, whiteSpace: "nowrap", flex: { xs: "1 1 100%", sm: "0 0 auto" } }}>
              New
            </Button>
          </Stack>
        </Stack>
      </Paper>

      {error ? <Alert severity="error">{error}</Alert> : null}

      <TableContainer
        component={Paper}
        elevation={0}
        sx={{
          borderRadius: 4,
          border: "1px solid rgba(118, 93, 68, 0.12)",
          background: "rgba(255, 250, 244, 0.96)"
        }}
      >
        <Table>
          <TableHead>
            <TableRow>
              {columns.map((column) => (
                <TableCell key={column.key} sx={{ fontWeight: 700 }}>
                  {column.label}
                </TableCell>
              ))}
              <TableCell sx={{ fontWeight: 700 }}>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {items.map((item) => (
              <TableRow key={String(item.id)}>
                {columns.map((column) => {
                  const value = item[column.key];
                  return (
                    <TableCell key={column.key}>
                      {renderColumnValue(column.key, value)}
                    </TableCell>
                  );
                })}
                <TableCell>
                  <Stack direction="row" spacing={1}>
                    {config.resource === "customers" ? (
                      <IconButton color="secondary" onClick={() => openEventForCustomer(item)} title="Create event">
                        <EventRoundedIcon />
                      </IconButton>
                    ) : null}
                    {config.resource === "events" ? (
                      <IconButton color="secondary" onClick={() => openQuotationForEvent(item)} title="Create quotation">
                        <DescriptionRoundedIcon />
                      </IconButton>
                    ) : null}
                    {config.resource === "events" && eventQuotationIds[String(item.id)] ? (
                      <IconButton color="secondary" onClick={() => openEventQuotationPrint(eventQuotationIds[String(item.id)])} title="Print quotation">
                        <PrintRoundedIcon />
                      </IconButton>
                    ) : null}
                    <IconButton color="primary" onClick={() => openEditDialog(item)}>
                      <EditRoundedIcon />
                    </IconButton>
                    <IconButton color="error" onClick={() => setDeleteTarget(item)}>
                      <DeleteOutlineRoundedIcon />
                    </IconButton>
                  </Stack>
                </TableCell>
              </TableRow>
            ))}
            {!items.length && !loading ? (
              <TableRow>
                <TableCell colSpan={columns.length + 1}>
                  <Typography sx={{ py: 4, textAlign: "center", color: "text.secondary" }}>
                    No data found.
                  </Typography>
                </TableCell>
              </TableRow>
            ) : null}
          </TableBody>
        </Table>
        <TablePagination
          component="div"
          count={totalItems}
          page={page}
          rowsPerPage={rowsPerPage}
          rowsPerPageOptions={[10, 25, 50, 100]}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
        />
      </TableContainer>

      <Dialog
        open={dialogOpen}
        onClose={closeDialog}
        fullWidth
        maxWidth="md"
      >
        <DialogTitle sx={{ pb: 1.5 }}>{editingItem ? `Edit ${config.title}` : `Create ${config.title}`}</DialogTitle>
        <DialogContent dividers>
          <Grid container spacing={2}>
            {config.formFields.map((field) => {
              const value = formState[field.name];
              const selectOptions = field.options || optionCache[field.name] || [];
              const fieldLockedByQuotation = isLockedEventRelationField(field.name);
              const helperText = formErrors[field.name] || (fieldLockedByQuotation ? "Locked because this event already has a quotation." : field.helperText);

              if (field.type === "checkbox") {
                return (
                  <Grid item xs={12} sm={6} key={field.name}>
                    <TextField
                      select
                      fullWidth
                      label={getFieldLabel(field)}
                      value={Boolean(value) ? "true" : "false"}
                      onChange={(event) => handleFieldChange(field.name, event.target.value === "true")}
                    >
                      <MenuItem value="true">Yes</MenuItem>
                      <MenuItem value="false">No</MenuItem>
                    </TextField>
                  </Grid>
                );
              }

              return (
                <Grid item xs={12} sm={field.type === "textarea" || field.type === "json" ? 12 : 6} key={field.name}>
                  <TextField
                    fullWidth
                    required={field.required || (field.requiredOnCreate && !editingItem)}
                    label={getFieldLabel(field)}
                    select={field.type === "select"}
                    multiline={field.type === "textarea" || field.type === "json"}
                    minRows={field.type === "json" ? 6 : field.type === "textarea" ? 4 : undefined}
                    type={field.type === "number" ? "number" : field.type === "datetime-local" ? "datetime-local" : field.type === "date" ? "date" : field.type === "email" ? "email" : "text"}
                    value={value ?? ""}
                    onChange={(event) => handleFieldChange(field.name, formatInputValue(field, event.target.value))}
                    disabled={field.readOnly || (field.generatedOnCreate && !editingItem) || fieldLockedByQuotation}
                    InputLabelProps={field.type === "date" || field.type === "datetime-local" ? { shrink: true } : undefined}
                    inputProps={{
                      min: field.min,
                      max: field.max,
                      maxLength: field.maxLength,
                      inputMode: field.pattern === "^\\d{10}$" ? "numeric" : undefined
                    }}
                    error={Boolean(formErrors[field.name])}
                    helperText={helperText}
                  >
                    {field.type === "select"
                      ? selectOptions.map((option) => (
                          <MenuItem key={String(option.value)} value={option.value}>
                            {option.label}
                          </MenuItem>
                        ))
                      : null}
                  </TextField>
                </Grid>
              );
            })}
          </Grid>
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button onClick={closeDialog}>Cancel</Button>
          <Button variant="contained" onClick={() => void handleSubmit()}>
            {editingItem ? "Save Changes" : "Create"}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={Boolean(deleteTarget)} onClose={() => setDeleteTarget(null)} fullWidth maxWidth="xs">
        <DialogTitle>Delete {config.title}</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete this record? This action cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button onClick={() => setDeleteTarget(null)}>Cancel</Button>
          <Button color="error" variant="contained" onClick={() => void handleDelete(deleteTarget?.id)}>
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </Stack>
  );
}
